import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from './PanelCard';
import { Sub, Table } from '../styles';
import {
  fetchBreakdown, formatNumber, hasValue, metaOfBreakdown, metricOf, usePanel,
} from '../config';
import { TAB_TASKS, processHealthHref, statusTarget } from '../../../components/performance/processHealthLinks';

// Spec §6.4 — "Süreç Adımlarında Tamamlama" (M03 aşama kırılımı: dönemdeki zorunlu aşama sayısı kadar halka, katalog sırasıyla,
// bağlayıcı çizgi; altında pay/payda kişi) ve "Aşamalara Göre Görev Dağılımı" (Tamamlandı / Devam Ediyor / Gecikmiş yığılmış çubuk).
// Tıklamalar E02'ye gider (spec §19/4-5): halka → Özet (M03 kartı o aşamada), dilim → Görevler (aşama + durum).
// Son inceleme (Önemli 2) — 9 aşamalı dönem dar kartta taşmasın: ızgara kendini satırlara böler (kart içinde kaydırma yok),
// halka SVG'si hücreyle küçülür (en çok 64 px). Bağlayıcı çizgi ilk satırın arkasından geçer.
const Rings = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
  gap: 12px 8px;
  position: relative;
  padding-top: 4px;

  &::before {
    content: '';
    position: absolute;
    left: 32px;
    right: 32px;
    top: 34px;
    height: 2px;
    background: ${({ theme }) => theme.colors.surface.border};
  }
`;

const RingItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  position: relative;
  text-align: center;
`;

const RingBox = styled.div`
  width: 100%;
  max-width: 64px;
`;

const RingLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const stageLabel = (t, key) => t(`performance.insight.stage.${key}`, { defaultValue: key });

function Ring({ pct }) {
  const theme = useTheme();
  const r = 26;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <svg width="100%" viewBox="0 0 64 64" role="img" aria-hidden="true" data-ring-svg="1" style={{ display: 'block' }}>
      <circle cx="32" cy="32" r={r} fill={theme.colors.surface.card} stroke={theme.colors.surface.border} strokeWidth="6" />
      <circle
        cx="32" cy="32" r={r} fill="none" stroke={theme.colors.primary.main} strokeWidth="6"
        strokeDasharray={`${(c * v) / 100} ${c}`} transform="rotate(-90 32 32)" strokeLinecap="round"
      />
      <text x="32" y="36" textAnchor="middle" fontSize={theme.metrics.font.size.xs} fontWeight={theme.metrics.font.weight.bold} fill={theme.colors.text.primary}>{`${Math.round(v)}%`}</text>
    </svg>
  );
}

const MaybeLink = ({ to, children, label }) => (to ? <Link to={to} aria-label={label}>{children}</Link> : children);

export function StageCompletionPanel({ filters, onMeta }) {
  const { t, i18n } = useTranslation();
  const [table, setTable] = useState(false);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchBreakdown(filters, ['M03'], 'stage'), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('stageCompletion', metaOfBreakdown(panel.data)); }, [panel.data, onMeta]);
  const cells = (panel.data?.cells || []).filter((c) => c.key);
  return (
    <PanelCard
      panelKey="stageCompletion" title={t('performance.operations.panel.stageCompletion')} span={7}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={cells.length === 0}
      emptyText={t('performance.operations.panel.noStages')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead><tr><th>{t('performance.operations.col.stage')}</th><th>{t('performance.operations.col.completion')}</th><th>{t('performance.operations.col.people')}</th></tr></thead>
          <tbody>
            {cells.map((c) => {
              const m = metricOf(c.metrics, 'M03') || c.metric;
              return (
                <tr key={c.key} data-row={c.key}>
                  <td>{stageLabel(t, c.key)}</td>
                  <td>{hasValue(m) ? `${formatNumber(m.value, i18n.language)}%` : t('performance.operations.kpi.noData')}</td>
                  <td>{hasValue(m) ? `${formatNumber(m.numerator, i18n.language, 0)} / ${formatNumber(m.denominator, i18n.language, 0)}` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <Rings>
          {cells.map((c) => {
            const m = metricOf(c.metrics, 'M03') || c.metric;
            return (
              <RingItem key={c.key} data-ring={c.key}>
                <RingBox>
                  <MaybeLink to={processHealthHref(filters, { m03Stage: c.key })} label={stageLabel(t, c.key)}>
                    <Ring pct={hasValue(m) ? m.value : 0} />
                  </MaybeLink>
                </RingBox>
                <RingLabel title={stageLabel(t, c.key)}>{stageLabel(t, c.key)}</RingLabel>
                <Sub>{hasValue(m) ? `${formatNumber(m.numerator, i18n.language, 0)} / ${formatNumber(m.denominator, i18n.language, 0)}` : t('performance.operations.kpi.noData')}</Sub>
              </RingItem>
            );
          })}
        </Rings>
      )}
    </PanelCard>
  );
}

const DIST_CODES = ['pm.tasksDone', 'pm.tasksInProgress', 'M04'];

export function StageTaskDistributionPanel({ filters, onMeta }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const [table, setTable] = useState(false);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchBreakdown(filters, DIST_CODES, 'stage'), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('stageDistribution', metaOfBreakdown(panel.data)); }, [panel.data, onMeta]);
  const rows = (panel.data?.cells || []).filter((c) => c.key).map((c) => ({
    key: c.key,
    stage: stageLabel(t, c.key),
    done: Number(metricOf(c.metrics, 'pm.tasksDone')?.value || 0),
    inProgress: Number(metricOf(c.metrics, 'pm.tasksInProgress')?.value || 0),
    overdue: Number(metricOf(c.metrics, 'M04')?.value || 0),
  }));
  const empty = rows.every((r) => r.done + r.inProgress + r.overdue === 0);
  const series = [
    // Devam Ediyor = pm.tasksInProgress: açık durumlar VE gecikmemiş (son inceleme Önemli 5) — statusTarget tek kural.
    { dataKey: 'done', name: t('performance.operations.series.done'), color: theme.colors.secondary.main, task: statusTarget('done') },
    { dataKey: 'inProgress', name: t('performance.operations.series.inProgress'), color: theme.colors.primary.main, task: statusTarget('inProgress') },
    { dataKey: 'overdue', name: t('performance.operations.series.overdue'), color: theme.colors.auxiliar.danger, task: statusTarget('overdue') },
  ];
  const go = (row, s) => {
    if (!row?.key) return;
    history.push(processHealthHref(filters, { tab: TAB_TASKS, task: { stage: row.key, ...s.task } }));
  };
  return (
    <PanelCard
      panelKey="stageDistribution" title={t('performance.operations.panel.stageDistribution')} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0 || empty}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead><tr><th>{t('performance.operations.col.stage')}</th>{series.map((s) => <th key={s.dataKey}>{s.name}</th>)}</tr></thead>
          <tbody>{rows.map((r) => <tr key={r.key} data-row={r.key}><td>{r.stage}</td><td>{r.done}</td><td>{r.inProgress}</td><td>{r.overdue}</td></tr>)}</tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: theme.metrics.font.size.xs }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              {series.map((s) => (
                <Bar
                  key={s.dataKey} dataKey={s.dataKey} name={s.name} stackId="tasks" fill={s.color}
                  cursor="pointer" onClick={(d) => go(d.payload || d, s)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}
